import type { JSX } from "react";
import { FC } from "react";
import { HeadingSize, H2 } from "../atoms/Heading";
import { P1 } from "../atoms/Paragraph";
import styled from "styled-components";
import { StandardPadding } from "../templates/StandardPadding";
import { StandardSection } from "../templates/StandardSection";

interface FactCardSectionProps {
  id?: string;
  title: string;
  content: string;
  cards: FactCardProps[];
  background?: string;
  pt?: string;
  pb?: string;
}

interface FactCardProps {
  top: JSX.Element;
  bottom: JSX.Element;
}

export const FactCardSection: FC<React.PropsWithChildren<FactCardSectionProps>> = ({
  id,
  title,
  content,
  cards,
  background,
  pt,
  pb,
}) => {
  return (
    <section id={id} aria-labelledby={`${id}-factcardsection-title`}>
      <StandardSection background={background} pt={pt} pb={pb}>
        <div
          style={{
            maxWidth: "58rem",
            margin: "0 auto",
            textAlign: "center",
          }}
        >
          <H2 id={`${id}-factcardsection-title`} size={HeadingSize.H1} align="center">
            {title}
          </H2>
          <P1 pb="5rem">{content}</P1>
        </div>
        <Grid>
          {!cards.length
            ? null
            : cards.map(({ top, bottom }, index) => (
                <FactCard key={`fact-card-${index}`} top={top} bottom={bottom} />
              ))}
        </Grid>
      </StandardSection>
    </section>
  );
};

export const FactCard: FC<React.PropsWithChildren<FactCardProps>> = ({ top, bottom }) => {
  return (
    <Card>
      <CardTop>{top}</CardTop>
      <hr style={{ borderTop: "0.125rem solid #D9D9D7", margin: 0 }} />
      <CardBottom>{bottom}</CardBottom>
    </Card>
  );
};

const Padding = styled(StandardPadding)`
  position: relative;
  max-width: 80rem;
  margin: 0 auto;

  @media only screen and (max-width: 40rem) {
    overflow: hidden;
  }
`;

const Card = styled.div`
  border: 0.125rem solid #d9d9d7;
  box-shadow: 0.5rem 0.5rem 0 #f5f5f5;
  border-radius: 0.5rem;
  background: #fafafe;

  transition: 200ms ease-out;

  &:hover {
    transform: translateY(-0.25rem);
  }
`;

const CardTop = styled.div`
  padding: 2rem;
  border-radius: 0.5rem 0.5rem 0 0;
  background: #fff;
`;

const CardBottom = styled.div`
  padding: 2rem;
  background: #fafafe;
  border-radius: 0 0 0.5rem 0.5rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  align-items: stretch;

  @media only screen and (max-width: 52rem) {
    grid-template-columns: 1fr;
  }
`;
